import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BaseComponent } from '@app/core/bases';
import { Product } from '@app/core/interfaces';
import { EbayService, ProductService, UtilityService } from '@app/core/services';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { TabStates } from '../product-detail/product-detail';
import { takeUntil } from 'rxjs';
import { EbayProduct, EbayProductCollection, EbayStock, EbayStockCollection } from '@app/core/interfaces/ebay';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockInput, InputBlockEmitter, BlockTextarea } from "@app/shared";
import { ProductDetailDistributionEbayStock } from "../product-detail-distribution-ebay-stock/product-detail-distribution-ebay-stock";
import { BadgeModule } from 'primeng/badge';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProductDetailInfosInput } from "@app/features/product/product-detail-infos-input/product-detail-infos-input";
import {
    ProductDetailDistributionEbayMarketplace
} from "@app/features/product/product-detail-distribution-ebay-marketplace/product-detail-distribution-ebay-marketplace";


@Component({
  selector: 'app-product-detail-distribution-ebay',
  imports: [CommonModule, ButtonModule, AccordionModule, ConfirmDialogModule, ProgressSpinnerModule, BlockInput, BlockTextarea, ProductDetailDistributionEbayStock, BadgeModule, TagModule, TooltipModule, ProductDetailInfosInput],
  providers: [ConfirmationService, DialogService],
  templateUrl: './product-detail-distribution-ebay.html'
})
export class ProductDetailDistributionEbay extends BaseComponent implements OnChanges {
  @Input() product!: Product;
  @Output() productChange: EventEmitter<Product> = new EventEmitter<Product>();

  @Input() tabStates!: TabStates[];
  @Output() tabStatesChange: EventEmitter<TabStates[]> = new EventEmitter<TabStates[]>();

  @Input() tabIndex!: number;

  @Output() needStockReload: EventEmitter<boolean> = new EventEmitter<boolean>();

  ebayProduct: EbayProduct | null = null;
  ebayStocks: EbayStock[] | null = null;

  lineActivities: InputBlockEmitter[] = [];

  isLoadingData: boolean = true;
  isLoadingSubData: boolean = true;
  isSendingData: boolean = false;

  constructor(
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private productService: ProductService,
    private ebayService: EbayService,
    public utilityService: UtilityService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabStates']) {
      const tab: TabStates = this.tabStates.find(state => state.index === this.tabIndex) as TabStates
      if (tab.active && !tab.loaded) {
        this.getEbayProduct();
      }
    }
  }

  onNeedStockReload(): void {
    this.needStockReload.emit(true)
  }

  onEbayStockChange(ebayStock: EbayStock): void {
    this.ebayStocks = this.ebayStocks?.map((es) => es.id === ebayStock.id ? ebayStock : es) as EbayStock[]
  }

  getEbayProduct(): void {
    this.isLoadingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.getEbayProductCollection({ produit_id: this.product.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (collection: EbayProductCollection) => {
          this.ebayProduct = collection.results.length > 0 ? collection.results[0] : null;
          this.product.produits_ebay = this.ebayProduct ? [this.ebayProduct] : [];
          this.productChange.emit(this.product);
          const tabIndex = this.tabStates.findIndex(state => state.index === this.tabIndex)
          this.tabStates[tabIndex].loaded = true;
          this.tabStatesChange.emit(this.tabStates);
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.getEbayStock();
          this.isLoadingData = false;
        }
      });
  }

  getEbayStock(): void {
    if (!this.ebayProduct) return;
    this.isLoadingSubData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.getEbayStockCollection({ produit_ebay_id: this.ebayProduct.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (collection: EbayStockCollection) => {
          this.ebayStocks = collection.results.length > 0 ? collection.results : null;
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingSubData = false;
        }
      });
  }

  regenerateEbay(): void {
    this.confirmationService.confirm({
      message: 'Cette action va réinitialiser le produit Ebay ainsi que tous ses stocks associés.<br> Êtes-vous sûr de vouloir continuer?',
      header: 'Confirmer la regénération du produit Ebay et de ses stocks',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: PrimeIcons.CHECK,
      acceptButtonProps: {
        iconPos: 'right'
      },
      rejectIcon: PrimeIcons.TIMES,
      rejectButtonProps: {
        text: true,
        severity: 'danger'
      },
      accept: () => {
        this.generateEbay();
      }
    });
  }

  generateEbay(): void {
    this.isSendingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService.postGenererEbay(this.product.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.getEbayProduct();
          this.isSendingData = false;
          this.toastService.add({ severity: 'success', summary: 'Génération du produit Ebay', detail: 'Le produit Ebay a été généré avec succès.' });
          this.onNeedStockReload();
        }
      });
  }

  updateEbayStocks(): void {
    this.isSendingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService.postGenererEbay(this.product.id, { update_only: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.getEbayProduct();
          this.isSendingData = false;
          this.toastService.add({ severity: 'success', summary: 'Mise à jour', detail: 'Mise à jour des stocks Ebay effectuée.' });
        }
      });
  }

    showEbayMarketplace(ebayStock: EbayStock): void {
      const data = { ebayStock: ebayStock };
        this.dialogService.open(ProductDetailDistributionEbayMarketplace, {
            header: `Détail des magasins du stock`,
            inputValues: data,
            width: '90%',
            modal: true,
            focusOnShow: false,
        });
    }

  sendingDataHandler(event: InputBlockEmitter) {
    if (event.property === 'designation' && event.isSendingData === false) {
      this.getEbayProduct();
    }

    const index = this.lineActivities.findIndex(line => line.property === event.property);
    if (index >= 0) this.lineActivities[index] = event;
    else this.lineActivities.push(event);

    if (event.message)
      this.toastService.add(event.message);

    this.productChange.emit(this.product);
  }
}
